<script setup lang="ts">
import {AnswerComment} from "@/types";
import {useDate} from "@/composables/date";
import {mergeProps, ref} from "vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {useAuth} from "@/composables/auth";
import {useRules} from "vuetify/labs/rules";

const rules = useRules()

const {lastUsed} = useDate()
const {user} = useAuth()

interface Props {
    comment: AnswerComment
    readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false
})
const emit = defineEmits<{
    (e: 'update', comment: string): void;
    (e: 'delete', commentId: number): void;
}>();

const form = ref<HTMLFormElement>()
const valid = ref<boolean>(false);
const isEdited = ref<boolean>(false)
const isDeleteCommentDialogOpen = ref<boolean>(false);
const comment = ref<string>(props.comment.comment)

const saveComment = async () => {
    const {valid} = await form.value?.validate()

    if (!valid) {
        return;
    }

    emit('update', comment.value)
    isEdited.value = false

    form.value?.resetValidation();
}

const resetComment = () => {
    comment.value = props.comment.comment
    isEdited.value = false
}
</script>

<template>
    <v-card
        variant="flat"
        color="white"
        border="sm"
        rounded="lg"
        width="100%"
    >
        <v-form
            ref="form"
            v-model="valid"
            class="pa-0 ma-0"
            @submit.prevent="saveComment"
        >
            <v-card-title class="pt-2 px-3 text-body-2 text-medium-emphasis">
                <span class="font-weight-bold text-black">
                    {{ props.comment.user_name }}
                </span>
                {{ lastUsed(props.comment.created_at) }}
                <v-menu
                    v-if="!isEdited && props.comment.user_id === user.id && !props.readonly"
                    location="top end"
                >
                    <template #activator="{ props: bind }">
                        <v-tooltip
                            location="top"
                            content-class="bg-black border-sm text-tooltip"
                            max-width="200"
                        >
                            <template #activator="{ props: subBind }">
                                <v-icon-btn
                                    v-bind="mergeProps(bind, subBind)"
                                    icon="mdi-dots-vertical"
                                    rounded="lg"
                                    color="white"
                                    icon-size="18"
                                    size="28"
                                    class="float-end"
                                />
                            </template>
                            More actions
                        </v-tooltip>
                    </template>
                    <v-list
                        rounded="lg"
                        border="sm"
                        elevation="3"
                        class="bg-background py-0 my-1"
                        width="250"
                    >
                        <v-list-item
                            class="text-body-2 ma-1 pl-2 text-error"
                            link
                            density="compact"
                            @click="isDeleteCommentDialogOpen = true"
                        >
                            Delete comment
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-tooltip
                    v-if="!isEdited && props.comment.user_id === user.id && !props.readonly"
                    location="top"
                    content-class="bg-black border-sm text-tooltip"
                    max-width="200"
                >
                    <template #activator="{ props: bind }">
                        <v-icon-btn
                            v-bind="bind"
                            icon="mdi-pencil-outline"
                            rounded="lg"
                            color="white"
                            icon-size="18"
                            size="28"
                            class="float-end"
                            @click="isEdited = !isEdited"
                        />
                    </template>
                    Edit comment
                </v-tooltip>
            </v-card-title>
            <v-card-text
                v-if="!isEdited"
                class="px-3 pb-3"
            >
                <div
                    class="text-body-2 text-pre-wrap"
                    :class="props.comment.is_edited ? 'mb-3' : ''"
                >
                    {{ props.comment.comment }}
                </div>
                <small
                    v-if="props.comment.is_edited"
                    class="text-medium-emphasis text-sm"
                >
                    Edited {{ lastUsed(props.comment.updated_at) }}
                </small>
            </v-card-text>
            <v-textarea
                v-if="isEdited"
                v-model="comment"
                placeholder="Write a comment..."
                rows="1"
                class="px-3 pb-3 pt-1"
                :rules="[rules.required(), rules.pattern(/^(?!\s*$).+/), rules.maxLength(1000)]"
                :hide-details="valid"
                auto-grow
            />
            <v-card-actions
                v-if="isEdited"
                class="px-3 pb-3 pt-0"
            >
                <v-btn
                    variant="flat"
                    :disabled="comment === '' || comment === null"
                    type="submit"
                >
                    Save comment
                </v-btn>
                <v-btn
                    color="white"
                    variant="flat"
                    border="sm"
                    type="button"
                    @click="resetComment"
                >
                    <span class="font-weight-regular">
                        Cancel
                    </span>
                </v-btn>
            </v-card-actions>
        </v-form>
    </v-card>
    <delete-dialog
        v-model="isDeleteCommentDialogOpen"
        @submit="emit('delete', props.comment.id)"
    >
        <template #title>
            Delete comment?
        </template>
        <template #default>
            Are you sure you want to delete this comment?
        </template>
        <template #label>
            Delete comment
        </template>
    </delete-dialog>
</template>

<style scoped>

</style>
